/* Community story player. The product card adds to cart via caene-placeholder-cart-add.js. */
(() => {
  'use strict';
  const instances = new Map();
  function setup(root) {
    if (instances.has(root)) return;
    const dialog = root.querySelector('[data-story-dialog]');
    const video = dialog?.querySelector('[data-story-video]');
    if (!dialog || !video) return;
    const controller = new AbortController();
    const on = (target, event, handler, options = {}) => target?.addEventListener(event, handler, { ...options, signal: controller.signal });
    const find = (selector) => dialog.querySelector(selector);
    const progress = find('[data-story-progress]');
    const status = find('[data-story-status]');
    const hint = find('[data-story-hint]');
    const mute = find('[data-story-mute]');
    const pause = find('[data-story-pause]');
    const expand = find('[data-story-expand]');
    const previous = find('[data-story-prev]');
    const next = find('[data-story-next]');
    let index = 0;
    let sources = [];
    let opener = null;
    let savedScroll = null;
    let savedStyle = null;
    let hintTimer;
    let loadTimer;
    let swipe = null;
    let generation = 0;
    let disposed = false;
    const message = (text) => { if (status) status.textContent = text; };
    const controls = () => {
      if (mute) mute.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4Z"/><path d="' + (video.muted ? 'm15 8 6 8m0-8-6 8' : 'M15 8q6 4 0 8M18 5q10 7 0 14') + '"/></svg>';
      if (pause) pause.textContent = video.paused ? '▶' : 'Ⅱ';
      mute?.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Mute sound');
      mute?.setAttribute('aria-pressed', String(!video.muted));
      pause?.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      pause?.setAttribute('aria-pressed', String(!video.paused));
      dialog.classList.toggle('is-muted', video.muted);
      dialog.classList.toggle('is-paused', video.paused);
    };
    function watchLoading() {
      clearTimeout(loadTimer);
      loadTimer = setTimeout(() => {
        if (!dialog.open || disposed) return;
        video.pause();
        dialog.classList.remove('is-loading');
        message('Video is taking longer to load. Try play or the next video.');
        controls();
      }, 15000);
    }
    function play() {
      const current = generation;
      const attempt = video.play();
      if (attempt?.catch) attempt.catch(() => {
        if (disposed || current !== generation || !dialog.open) return;
        clearTimeout(loadTimer);
        message('Press play to start the video.');
        dialog.classList.remove('is-loading');
        controls();
      });
    }
    function collect() {
      const seen = new Set();
      sources = [...root.querySelectorAll('.pg__community [data-story-src]')].filter((trigger) => {
        const src = trigger.dataset.storySrc;
        if (!src || seen.has(src)) return false;
        seen.add(src);
        return true;
      });
    }
    function load(position) {
      if (position < 0 || position >= sources.length) return;
      generation += 1;
      clearTimeout(loadTimer);
      index = position;
      const item = sources[index].dataset;
      video.pause();
      video.poster = item.storyPoster || '';
      video.setAttribute('aria-label', item.storyTitle || 'Community video');
      video.src = item.storySrc;
      if (progress) progress.value = 0;
      if (previous) previous.disabled = index === 0;
      if (next) next.disabled = index === sources.length - 1;
      const title = find('[data-story-product-title]');
      const price = find('[data-story-product-price]');
      const image = find('[data-story-product-image]');
      const product = find('[data-story-product]');
      if (title) title.textContent = item.storyProductTitle || '';
      if (price) price.textContent = item.storyProductPrice || '';
      if (image) {
        image.alt = item.storyProductTitle || '';
        if (item.storyProductImage) { image.src = item.storyProductImage; image.hidden = false; }
        else { image.removeAttribute('src'); image.hidden = true; }
      }
      const addButton = find('[data-story-add]');
      if (addButton) {
        if (item.storyProductVariant) addButton.dataset.variantId = item.storyProductVariant;
        else delete addButton.dataset.variantId;
        addButton.disabled = false;
        addButton.hidden = !item.storyProductVariant;
      }
      if (product) product.hidden = !item.storyProductTitle;
      dialog.classList.add('is-loading');
      message('Loading video…');
      watchLoading();
      video.load();
      play();
      controls();
    }
    function lockScroll() {
      savedScroll = { x: window.scrollX, y: window.scrollY };
      savedStyle = document.body.getAttribute('style');
      Object.assign(document.body.style, { position: 'fixed', top: `-${savedScroll.y}px`, left: `-${savedScroll.x}px`, width: '100%', overflow: 'hidden' });
    }
    function unlockScroll() {
      if (!savedScroll) return;
      if (savedStyle === null) document.body.removeAttribute('style');
      else document.body.setAttribute('style', savedStyle);
      const { x, y } = savedScroll;
      savedScroll = null;
      window.scrollTo({ left: x, top: y, behavior: 'instant' });
    }
    function reset() {
      generation += 1;
      clearTimeout(hintTimer);
      clearTimeout(loadTimer);
      video.pause();
      video.removeAttribute('src');
      video.load();
      dialog.classList.remove('is-expanded', 'is-loading');
      expand?.setAttribute('aria-expanded', 'false');
      expand?.setAttribute('aria-label', 'Expand video');
      message('');
      unlockScroll();
      if (opener?.isConnected) opener.focus({ preventScroll: true });
      opener = null;
      document.dispatchEvent(new Event('visibilitychange'));
    }
    function close() { if (dialog.open) dialog.close(); }
    function open(trigger) {
      if (dialog.open) return;
      collect();
      let position = sources.findIndex((item) => item.dataset.storySrc === trigger.dataset.storySrc);
      if (position < 0) { sources.push(trigger); position = sources.length - 1; }
      opener = trigger;
      video.muted = true;
      dialog.showModal();
      document.querySelectorAll('video').forEach((preview) => { if (preview !== video) preview.pause(); });
      lockScroll();
      if (hint) hint.hidden = false;
      clearTimeout(hintTimer);
      hintTimer = setTimeout(() => { if (hint) hint.hidden = true; }, 2600);
      load(position);
      find('[data-story-close]')?.focus({ preventScroll: true });
    }
    on(root, 'click', (event) => {
      const trigger = event.target.closest('[data-story-src]');
      if (!trigger || !root.contains(trigger)) return;
      event.preventDefault();
      open(trigger);
    });
    on(find('[data-story-close]'), 'click', close);
    on(dialog, 'cancel', (event) => { event.preventDefault(); close(); });
    on(dialog, 'close', reset);
    on(dialog, 'click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
    });
    on(mute, 'click', () => { video.muted = !video.muted; controls(); });
    on(pause, 'click', () => { if (video.paused) play(); else video.pause(); });
    on(expand, 'click', () => {
      const expanded = dialog.classList.toggle('is-expanded');
      expand.setAttribute('aria-expanded', String(expanded));
      expand.setAttribute('aria-label', expanded ? 'Reduce video' : 'Expand video');
    });
    on(previous, 'click', () => load(index - 1));
    on(next, 'click', () => load(index + 1));
    on(dialog, 'keydown', (event) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        load(index + (event.key === 'ArrowDown' ? 1 : -1));
      }
    });
    on(dialog, 'touchstart', (event) => {
      swipe = null;
      if (event.touches.length !== 1 || event.target.closest('button, a, input, [data-story-product]')) return;
      swipe = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }, { passive: true });
    on(dialog, 'touchend', (event) => {
      if (!swipe || !event.changedTouches.length) return;
      const dx = event.changedTouches[0].clientX - swipe.x;
      const dy = event.changedTouches[0].clientY - swipe.y;
      swipe = null;
      if (Math.abs(dy) > 55 && Math.abs(dy) > Math.abs(dx) * 1.3) {
        if (hint) hint.hidden = true;
        load(index + (dy < 0 ? 1 : -1));
      }
    }, { passive: true });
    on(dialog, 'touchcancel', () => { swipe = null; });
    on(video, 'timeupdate', () => { if (progress) progress.value = Number.isFinite(video.duration) && video.duration > 0 ? video.currentTime / video.duration * 100 : 0; });
    on(video, 'playing', () => { clearTimeout(loadTimer); dialog.classList.remove('is-loading'); message(''); controls(); });
    on(video, 'play', controls);
    on(video, 'pause', controls);
    on(video, 'volumechange', controls);
    on(video, 'waiting', () => { if (dialog.open) { dialog.classList.add('is-loading'); message('Loading video…'); watchLoading(); } });
    on(video, 'canplay', () => { dialog.classList.remove('is-loading'); if (dialog.open && !video.paused) message(''); });
    on(video, 'error', () => { clearTimeout(loadTimer); if (dialog.open) { dialog.classList.remove('is-loading'); message('This video could not load. Try the next video.'); } });
    on(video, 'ended', () => { if (index < sources.length - 1) load(index + 1); else { message('End of video. Choose a previous story or replay.'); controls(); } });
    on(document, 'visibilitychange', () => { if (document.hidden && dialog.open) video.pause(); });
    instances.set(root, () => {
      disposed = true;
      if (dialog.open) { dialog.close(); reset(); }
      clearTimeout(hintTimer);
      clearTimeout(loadTimer);
      controller.abort();
      instances.delete(root);
    });
  }
  const scan = (scope) => {
    if (scope.matches?.('.pg--caene')) setup(scope);
    scope.querySelectorAll?.('.pg--caene').forEach(setup);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [root, dispose] of instances) if (event.target === root || event.target.contains(root)) dispose();
  });
})();
